import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import cn from "classnames";
import { ArrowDown2, ArrowRight2 } from "iconsax-react";
import SidebarFlyout from "./SidebarFlyout";
import type { SidebarDropdown, SidebarLink, SidebarSubmenu } from "./types";

function isChildActive(child: SidebarLink | SidebarSubmenu, pathname: string): boolean {
  if (child.type === "submenu") {
    return child.children.some(
      (sub) => pathname === sub.href || pathname.startsWith(sub.href + "/"),
    );
  }
  return pathname === child.href || pathname.startsWith(child.href + "/");
}

function collectLeafHrefs(children: (SidebarLink | SidebarSubmenu)[]): string[] {
  return children.flatMap((child) => (child.type === "submenu" ? child.children.map((sub) => sub.href) : [child.href]));
}

function resolveActiveHref(hrefs: string[], pathname: string): string | null {
  let best: string | null = null;
  for (const href of hrefs) {
    if ((pathname === href || pathname.startsWith(href + "/")) && (best === null || href.length > best.length)) {
      best = href;
    }
  }
  return best;
}

export default function SidebarDropdownItem({
  item,
  isOpen,
  onToggle,
  location,
  isCollapsed,
  onExpand,
}: {
  item: SidebarDropdown;
  isOpen: boolean;
  onToggle: () => void;
  location: { pathname: string };
  isCollapsed?: boolean;
  onExpand?: () => void;
}) {
  const Icon = item.icon;
  const { t } = useTranslation("Sidebar");
  const isActive = item.children.some((child) => isChildActive(child, location.pathname));
  const activeHref = resolveActiveHref(collectLeafHrefs(item.children), location.pathname);

  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const submenuTriggerRefs = useRef<Record<string, HTMLDivElement | null>>({});

  function clearCloseTimer() {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }

  function handleSubmenuMouseEnter(label: string) {
    clearCloseTimer();
    setActiveSubmenu(label);
  }

  function handleSubmenuMouseLeave() {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => setActiveSubmenu(null), 100);
  }

  function handleFlyoutMouseEnter() {
    clearCloseTimer();
  }

  function handleFlyoutMouseLeave() {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => setActiveSubmenu(null), 100);
  }

  useEffect(function closeOnNavigation() {
    setActiveSubmenu(null);
  }, [location.pathname]);

  useEffect(function cleanupTimerOnUnmount() {
    return () => clearCloseTimer();
  }, []);

  function handleClick() {
    if (isCollapsed && onExpand) {
      onExpand();
    } else {
      onToggle();
    }
  }

  return (
    <div className="flex flex-col">
      <button
        type="button"
        onClick={handleClick}
        title={isCollapsed ? t(item.label) : undefined}
        className={cn(
          "group flex items-center py-1.5 rounded-lg cursor-pointer transition-[padding] duration-300 outline-none focus-visible:ring-2 focus-visible:ring-brand-200",
          isActive
            ? "text-body-bold-sm bg-brand-500/15 content-base-primary"
            : "text-body-regular-sm content-base-primary hover:bg-brand-500/15",
          isCollapsed ? "justify-center" : "justify-between px-2",
        )}>
        <div
          className={cn(
            "flex items-center overflow-hidden transition-[gap] duration-300",
            isCollapsed ? "gap-0" : "gap-2",
          )}>
          {Icon && (
            <span className={cn("shrink-0 duration-250", isActive ? "content-action-brand" : "content-base-primary")}>
              <Icon size={isCollapsed ? 18 : 16} variant={isActive ? "Bulk" : "Linear"} color="currentColor" />
            </span>
          )}
          <span
            className={cn(
              "text-left whitespace-nowrap overflow-hidden transition-[opacity,width] duration-300",
              isCollapsed ? "opacity-0 w-0" : "opacity-100",
            )}>
            {t(item.label)}
          </span>
        </div>

        <span
          className={cn(
            "transition-[transform,opacity,width] duration-250 overflow-hidden",
            isActive ? "content-base-secondary" : "content-base-primary",
            isOpen && !isCollapsed && "rotate-180",
            isCollapsed ? "opacity-0 w-0" : "opacity-100",
          )}>
          <ArrowDown2 size={16} color="currentColor" />
        </span>
      </button>

      <div
        className={cn(
          "flex flex-col gap-1 ml-4 mt-0.5 overflow-hidden transition-[max-height,opacity] duration-250",
          isOpen && !isCollapsed ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0",
        )}>
        {item.children.map(function renderChild(child) {
          if (child.type === "submenu") {
            return (
              <SidebarSubmenuItem
                key={child.label}
                child={child}
                activeHref={activeHref}
                activeSubmenu={activeSubmenu}
                submenuTriggerRefs={submenuTriggerRefs}
                onMouseEnter={handleSubmenuMouseEnter}
                onMouseLeave={handleSubmenuMouseLeave}
                onFlyoutMouseEnter={handleFlyoutMouseEnter}
                onFlyoutMouseLeave={handleFlyoutMouseLeave}
              />
            );
          }

          const isLinkActive = child.href === activeHref;
          const ChildIcon = child.icon;

          return (
            <Link
              key={child.label}
              to={child.href}
              className={cn(
                "flex items-center gap-1.5 px-4 py-[3px] outline-none focus-visible:ring-2 focus-visible:ring-brand-200",
                isLinkActive
                  ? "text-body-bold-sm content-base-primary border-l-[1.5px] border-brand-500 last:mb-1"
                  : "text-body-regular-sm content-base-secondary border-l surface-container-stroke last:mb-1",
              )}>
              {ChildIcon && (
                <span className={cn("shrink-0", isLinkActive && "content-action-brand")}>
                  <ChildIcon size={14} variant={isLinkActive ? "Bulk" : "Linear"} color="currentColor" />
                </span>
              )}
              <span>{t(child.label)}</span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function SidebarSubmenuItem({
  child,
  activeHref,
  activeSubmenu,
  submenuTriggerRefs,
  onMouseEnter,
  onMouseLeave,
  onFlyoutMouseEnter,
  onFlyoutMouseLeave,
}: {
  child: SidebarSubmenu;
  activeHref: string | null;
  activeSubmenu: string | null;
  submenuTriggerRefs: React.RefObject<Record<string, HTMLDivElement | null>>;
  onMouseEnter: (label: string) => void;
  onMouseLeave: () => void;
  onFlyoutMouseEnter: () => void;
  onFlyoutMouseLeave: () => void;
}) {
  const { t } = useTranslation("Sidebar");
  const SubmenuIcon = child.icon;
  const isSubmenuActive = child.children.some((sub) => sub.href === activeHref);
  const triggerRef = useRef<HTMLDivElement>(null);

  useEffect(function syncRef() {
    submenuTriggerRefs.current[child.label] = triggerRef.current;
  });

  return (
    <div
      ref={triggerRef}
      onMouseEnter={() => onMouseEnter(child.label)}
      onMouseLeave={onMouseLeave}
      className={cn(
        "flex items-center gap-1.5 px-4 py-[3px] cursor-pointer last:mb-1",
        isSubmenuActive
          ? "text-body-bold-sm content-base-primary border-l-[1.5px] border-brand-500"
          : "text-body-regular-sm content-base-secondary border-l surface-container-stroke hover:content-base-primary",
      )}>
      {SubmenuIcon && (
        <span className={cn("shrink-0", isSubmenuActive && "content-action-brand")}>
          <SubmenuIcon size={14} variant={isSubmenuActive ? "Bulk" : "Linear"} color="currentColor" />
        </span>
      )}
      <span className="flex-1">{t(child.label)}</span>
      <ArrowRight2 size={12} color="currentColor" />
      {activeSubmenu === child.label && (
        <SidebarFlyout
          items={child.children}
          triggerRef={triggerRef}
          onMouseEnter={onFlyoutMouseEnter}
          onMouseLeave={onFlyoutMouseLeave}
        />
      )}
    </div>
  );
}
